import PubBaseLayout from "@pub/BaseLayout";
import { configGTM, configHome, configSite } from "@pub/config";
import { GlobalBaseData, ImageCrop } from "@sphtech/dmg-design-system";
import {
  CustomContext,
  ResponseType,
  TRouteWithRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import MetaData from "@src/app/components/Modules/MetaData";
import ErrorLayout from "@src/app/layouts/ErrorLayout/ErrorLayout";
import HomeLayout from "@src/app/layouts/Home/HomeLayout";
import { TeaserData } from "@src/app/types/ServerCustomContext";
import AdSettings from "@src/app/utils/AdSettings";
import {
  GA4_REF_CODE_ANCHORBLOCK_A,
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_LATEST_BLOCKS,
} from "@src/app/utils/ga/ref-code";
import {
  buildUrlWithGARefCode,
  getContentBlockRefCode,
} from "@src/app/utils/ga/tracking";
import { HomeElementConfigData } from "@src/app/utils/homeConfig";
import { createTeaserMapper } from "@src/app/utils/mapTeaserDataToProps";
import { Helmet } from "react-helmet-async";

export type HomeData = {
  topStories: TeaserData[];
  socialVideo?: {
    type: "list";
    key: string;
    title: string;
    titleZH: string;
    url?: string;
    teasers?: {
      embedUri?: string;
    }[];
  };
  blocks: {
    type: "list" | "section" | "tag";
    section: string;
    key: string;
    title: string;
    titleZH?: string;
    url?: string;
    teasers?: TeaserData[];
    code: string;
    cta?: boolean;
    config?: HomeElementConfigData;
    backgroundImage?: ImageCrop;
  }[];
};

export default function HomePage() {
  const routeContext: CustomContext<
    TRouteWithRedirect<HomeData & GlobalBaseData, string>
  > = useRouteContext();
  const { context: response } = routeContext;

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const spotlightStory = response.payload.blocks.find(
    (block) => block.key === "spotlight_story",
  );

  const topStory = response.payload.topStories[0];
  // Filter out based on displayPublishDate configuration
  const teaserOverwrites = configHome.teaser?.displayPublishDate
    ? undefined
    : {
        publishDate: undefined,
        updateDate: undefined,
      };

  const pageTitle = configSite.tagline;
  return (
    <PubBaseLayout>
      <Helmet>
        <meta
          name="google-site-verification"
          content={configGTM.siteVerification}
        />
      </Helmet>
      <MetaData
        title={pageTitle}
        path=""
        type="WebPage"
        description={configSite.description}
      />
      <AdSettings
        pageType="homepage"
        sectionName="Homepage"
        adUnits="lb1,lb2,imu1,imu2,imu3,catfish,prestitial"
        pageTitle={pageTitle}
        currentLocation={{
          pathname: "/",
        }}
      />

      <HomeLayout
        data={{
          AnchorBlockProps: topStory
            ? createTeaserMapper({
                gaRefCode: GA4_REF_CODE_ANCHORBLOCK_A,
                overwrites: teaserOverwrites,
              })(topStory)
            : undefined,
          latestArticle: {
            items: response.payload.topStories.slice(1).map((teaser) => {
              return createTeaserMapper({
                gaRefCode:
                  configHome.latestArticle.refCode ||
                  GA4_REF_CODE_LATEST_BLOCKS,
                overwrites: teaserOverwrites,
              })(teaser);
            }),
            viewMore: {
              url: buildUrlWithGARefCode(
                "/latest-stories",
                configHome.latestArticle.refCode || GA4_REF_CODE_LATEST_BLOCKS,
              ),
              text: "VIEW ALL",
            },
          },
          magazineIssues: {
            type: "list",
            title: response.payload.magazineIssues?.title || "",
            titleZH: response.payload.magazineIssues?.titleZH,
            url: response.payload.magazineIssues?.url || "",
            subscriptionInfo: {
              heading:
                response.payload.magazineIssues?.config?.home?.title || "",
              description:
                response.payload.magazineIssues?.config?.home?.subtitle || "",
              buttonText:
                response.payload.magazineIssues?.config?.home?.buttonText || "",
              buttonUrl:
                response.payload.magazineIssues?.config?.home?.buttonUrl || "",
              hideSubscriptionInfo:
                response.payload.magazineIssues?.config?.home
                  ?.hideSubscriptionInfo || "",
            },
            items:
              response.payload.magazineIssues?.teasers?.map((issue) => {
                const crop =
                  issue.image?.crops.original || issue.image?.crops.portrait;
                return {
                  url: issue.path || "",
                  image: crop?.src || "",
                  srcWidth: crop?.srcWidth || 0,
                  srcHeight: crop?.srcHeight || 0,
                  alt: issue.image?.alt || "",
                  caption: issue.title || "",
                  description: issue.description || "",
                };
              }) || [],
          },
          userSubmissionBox: response.payload.userSubmissionBox,
          socialVideo: response.payload.socialVideo && {
            type: "list",
            title: response.payload.socialVideo.title || "",
            titleZH: response.payload.socialVideo.titleZH || "",
            url: response.payload.socialVideo.url || "",
            items:
              response.payload.socialVideo.teasers?.map((teaser) => ({
                embedUrl: teaser.embedUri || "",
              })) || [],
          },
          contentBlock: response.payload.blocks.map((block) => ({
            type: block.type,
            title: block.title,
            titleZH: block.titleZH,
            uniqSectionName: block.section.startsWith("section-")
              ? block.section.replace("section-", "")
              : undefined,
            key: block.key,
            url: buildUrlWithGARefCode(
              block.url || "",
              getContentBlockRefCode(block.code),
            ),
            cta: block.cta !== false,
            items: block.teasers
              ? block.teasers.map(
                  createTeaserMapper({
                    gaRefCode: getContentBlockRefCode(block.code),
                    overwrites: teaserOverwrites,
                  }),
                )
              : [],
            withImu: false,
            config: block.config,
            backgroundImage: block.backgroundImage,
          })),
          spotlightStoryProps: spotlightStory?.teasers?.[0]
            ? createTeaserMapper({
                gaRefCode: GA4_REF_CODE_ANCHORBLOCK_B,
                overwrites: teaserOverwrites,
              })(spotlightStory.teasers[0])
            : undefined,
        }}
      />
    </PubBaseLayout>
  );
}
